/**
 * Onboarding wizard — channel integration steps.
 * Status is read from GET /api/connections (PostgreSQL), never from seeded localStorage.
 */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChannelPairingDialog } from "@/components/connections/ChannelPairingDialog";
import { TelegramConnectForm, useTelegramConfigure } from "@/components/connections/TelegramConnectPanel";
import { useBackendConnections, CONNECTION_LABELS } from "@/hooks/useBackendConnections";
import { channelsApi, oauthApi, onboardingApi } from "@/lib/api";
import { MessageCircle, Linkedin, Mail, Calendar, CheckCircle2, Loader2, AlertCircle, Send, ShieldCheck } from 'lucide-react';
import { QuoraConnectForm, useQuoraConfigure } from "@/components/connections/QuoraConnectPanel";
import { OnboardingStepBadge } from "@/components/onboarding/OnboardingStepBadge";
import { pairingErrorDescription } from "@/lib/pairingErrors";
import { useAssistantName } from "@/store/useTeamStore";

function isMockIdentity(identity) {
  if (!identity) return false;
  return /\(mock\)/i.test(identity) || /mock\.openclaw/i.test(identity);
}

function gmailDisplayIdentity(row) {
  if (!row?.configured) return null;
  const g = row.channel_config?.google_oauth || {};
  const email = row.google_email || g.google_email || row.account_identifier;
  if (!email || email.endsWith("@local.sellix")) return null;
  return email;
}

function gmailVerifiedSubtitle(row) {
  if (!row?.configured) return null;
  const at = row.oauth_validated_at || row.channel_config?.google_oauth?.oauth_validated_at;
  if (!at) return null;
  try {
    return `Last verified ${new Date(at).toLocaleString()}`;
  } catch {
    return null;
  }
}

function telegramDisplayIdentity(row) {
  if (!row) return null;
  const tg = row.channel_config?.telegram;
  if (tg?.bot_username) return tg.bot_username;
  if (row.bot_username) return row.bot_username;
  if (row.account_identifier && !isMockIdentity(row.account_identifier)) {
    return row.account_identifier;
  }
  return null;
}

function whatsappDisplayIdentity(row) {
  if (!row) return null;
  const wa = row.channel_config?.whatsapp;
  if (wa?.phone_number && !isMockIdentity(wa.phone_number)) return wa.phone_number;
  if (row.account_identifier && !isMockIdentity(row.account_identifier)) {
    return row.account_identifier;
  }
  return null;
}

function StatusLine({
  configured, identity, subtitle, pendingLabel = "Not configured yet", connectedLabel = "Configured",
}) {
  if (configured) {
    return (
      <span className="inline-flex flex-col gap-0.5 text-emerald-700">
        <span className="inline-flex items-center gap-1">
          <CheckCircle2 size={11} />
          {connectedLabel}{identity ? ` · ${identity}` : ""}
        </span>
        {subtitle && <span className="text-[10.5px] text-emerald-600">{subtitle}</span>}
      </span>
    );
  }
  return <span className="text-slate-500">{pendingLabel}</span>;
}

StatusLine.propTypes = {
  configured: PropTypes.bool,
  identity: PropTypes.string,
  subtitle: PropTypes.string,
  pendingLabel: PropTypes.string,
  connectedLabel: PropTypes.string,
};

function LoadingRow() {
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-3 text-[12px] text-slate-500">
      <Loader2 size={16} className="animate-spin text-indigo-600" />
      Loading connection status…
    </div>
  );
}

function ErrorRow({ message, onRetry }) {
  return (
    <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-2 text-[12px] text-rose-800">
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold">Couldn&apos;t load integrations</div>
        <div className="mt-0.5">{message}</div>
        {onRetry && (
          <Button size="sm" variant="outline" className="h-7 mt-2 text-[11px]" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

ErrorRow.propTypes = {
  message: PropTypes.string,
  onRetry: PropTypes.func,
};

function usePairingState(refresh, { ensureConnection } = {}) {
  const [pairingConn, setPairingConn] = useState(null);
  const [pairingPayload, setPairingPayload] = useState(null);
  const [pairingBusy, setPairingBusy] = useState(false);

  const closePairing = useCallback((open) => {
    if (!open) {
      setPairingConn(null);
      setPairingPayload(null);
    }
  }, []);

  const startPair = async (row, kind) => {
    setPairingBusy(true);
    let connRow = row;
    try {
      if (!connRow?.id && ensureConnection) {
        connRow = await ensureConnection(kind);
      }
      if (!connRow?.id) {
        toast.error(`${CONNECTION_LABELS[kind] || kind} is not ready — try refreshing the page`);
        return;
      }

      const oc = await onboardingApi.openclawStatus().catch(() => null);
      if (oc && !oc.gateway_reachable) {
        toast.error("OpenClaw gateway is not reachable", {
          description: oc.error || "Complete OpenClaw setup before pairing WhatsApp.",
        });
        return;
      }

      setPairingConn({ id: connRow.id, kind });
      setPairingPayload(null);

      const r = await channelsApi.pair(connRow.id);
      if (r.already_connected || r.status === "paired") {
        await refresh();
        const phone = r.phone_number;
        toast.success("WhatsApp configured", {
          description: phone ? `Connected: ${phone}` : "Connected successfully",
        });
        closePairing(false);
        return;
      }
      setPairingPayload(r);
    } catch (e) {
      closePairing(false);
      toast.error(e?.message || `Couldn't start ${CONNECTION_LABELS[kind]} pairing`, {
        description: pairingErrorDescription(e),
      });
    } finally {
      setPairingBusy(false);
    }
  };

  const onPaired = async (deviceLabel) => {
    await refresh();
    const phone = deviceLabel && !/\(mock\)/i.test(deviceLabel) ? deviceLabel : null;
    toast.success("WhatsApp configured", {
      description: phone ? `Connected: ${phone}` : "Connected successfully",
    });
    closePairing(false);
  };

  const retryPair = async () => {
    if (!pairingConn?.id) return;
    setPairingPayload(null);
    setPairingBusy(true);
    try {
      const r = await channelsApi.pair(pairingConn.id);
      setPairingPayload(r);
    } catch (e) {
      toast.error(e?.message || "Couldn't restart pairing");
    } finally {
      setPairingBusy(false);
    }
  };

  return {
    pairingConn,
    pairingPayload,
    pairingBusy,
    startPair,
    closePairing,
    onPaired,
    retryPair,
  };
}

function WhatsAppConnectionCard({
  loading,
  error,
  onRetry,
  configured,
  waIdentity,
  waSubtitle,
  waPendingLabel,
  canEdit,
  pairingBusy,
  seeding,
  onPair,
  row,
  waBusyLabel,
  waPairLabel,
  disabledReason,
}) {
  if (loading) return <LoadingRow />;
  if (error) return <ErrorRow message={error} onRetry={onRetry} />;

  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/40 p-5">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-emerald-100 text-emerald-700">
          <MessageCircle size={20} />
        </div>
        <div className="flex-1">
          <div className="text-[14px] font-semibold text-slate-900">WhatsApp</div>
          <div className="text-[11.5px]">
            <StatusLine
              configured={configured}
              identity={waIdentity}
              subtitle={waSubtitle}
              pendingLabel={waPendingLabel}
            />
          </div>
        </div>
        {!configured ? (
          <Button
            size="sm"
            className="h-8 text-[12px]"
            disabled={!canEdit || pairingBusy || loading || seeding}
            onClick={() => onPair(row, "whatsapp")}
            data-testid="onboarding-pair-whatsapp"
          >
            {pairingBusy || seeding ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" />
                {waBusyLabel}
              </span>
            ) : (
              waPairLabel
            )}
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="h-8 text-[12px]" disabled>
            Configured
          </Button>
        )}
      </div>
      <ol className="mt-4 space-y-1 text-[11px] text-slate-600 list-decimal list-inside">
        <li>Click <span className="font-medium">Pair WhatsApp</span> to generate a QR code</li>
        <li>On your phone: WhatsApp → Settings → Linked Devices → Link a Device</li>
        <li>Scan the QR — status updates automatically when pairing succeeds</li>
      </ol>
      {disabledReason && (
        <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2.5 py-1.5">
          {disabledReason}
        </p>
      )}
    </div>
  );
}

WhatsAppConnectionCard.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func,
  configured: PropTypes.bool,
  waIdentity: PropTypes.string,
  waSubtitle: PropTypes.string,
  waPendingLabel: PropTypes.string,
  canEdit: PropTypes.bool,
  pairingBusy: PropTypes.bool,
  seeding: PropTypes.bool,
  onPair: PropTypes.func,
  row: PropTypes.object,
  waBusyLabel: PropTypes.string,
  waPairLabel: PropTypes.string,
  disabledReason: PropTypes.string,
};

export function StepWhatsApp() {
  const maya = useAssistantName();
  const { getRow, getStatus, isConfigured, loading, error, refresh, canEdit, ensureConnection, seeding } = useBackendConnections();
  const row = getRow("whatsapp");
  const configured = isConfigured("whatsapp");
  const status = getStatus("whatsapp");
  const waIdentity = whatsappDisplayIdentity(row);
  const waName = row?.channel_config?.whatsapp?.push_name;
  const pairing = usePairingState(refresh, { ensureConnection });
  const disabledReason = !canEdit
    ? "Only workspace admins can pair WhatsApp"
    : null;

  let waSubtitle;
  if (configured && waName) waSubtitle = `Connected · ${waName}`;
  else if (configured) waSubtitle = "Connected successfully";

  const waPendingLabel = status === "pairing" ? "Pairing in progress…" : "Not configured — scan QR to pair";
  const waBusyLabel = seeding ? "Preparing…" : "Starting…";
  const waPairLabel = status === "pairing" ? "Continue pairing" : "Pair WhatsApp";

  return (
    <div>
      <OnboardingStepBadge stepId={4} />
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">Where most of your customers are</div>
      <p className="text-[13px] text-slate-600 mt-1">
        WhatsApp is where warm conversations happen. Pair your number — {maya} replies in your voice, 24/7.
      </p>
      <WhatsAppConnectionCard
        loading={loading}
        error={error}
        onRetry={refresh}
        configured={configured}
        waIdentity={waIdentity}
        waSubtitle={waSubtitle}
        waPendingLabel={waPendingLabel}
        canEdit={canEdit}
        pairingBusy={pairing.pairingBusy}
        seeding={seeding}
        onPair={pairing.startPair}
        row={row}
        waBusyLabel={waBusyLabel}
        waPairLabel={waPairLabel}
        disabledReason={disabledReason}
      />
      <div className="mt-3 text-center">
        <span className="text-[11px] text-slate-400">
          Don&apos;t have one ready? Continue with the button below — you can pair anytime from Settings → Connections.
        </span>
      </div>
      <ChannelPairingDialog
        open={!!pairing.pairingConn}
        connection={pairing.pairingConn}
        pairing={pairing.pairingPayload}
        loading={pairing.pairingBusy && !pairing.pairingPayload}
        onOpenChange={pairing.closePairing}
        onPaired={pairing.onPaired}
        onRetry={pairing.retryPair}
      />
    </div>
  );
}

export function StepTelegram() {
  const {
    getRow, isConfigured, loading, error, refresh, canEdit, ensureConnection,
  } = useBackendConnections();
  const row = getRow("telegram");
  const configured = isConfigured("telegram");
  const tgIdentity = telegramDisplayIdentity(row);
  const tgMeta = row?.channel_config?.telegram || {};

  const tg = useTelegramConfigure({
    connectionId: row?.id,
    ensureConnection,
    canEdit,
    onConfigured: refresh,
  });

  let tgContent;
  if (loading) {
    tgContent = <LoadingRow />;
  } else if (error) {
    tgContent = <ErrorRow message={error} onRetry={refresh} />;
  } else {
    tgContent = (
      <div className="mt-5 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/40 p-5 space-y-4">
        {configured ? (
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-cyan-100 text-cyan-700">
              <Send size={20} />
            </div>
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-slate-900">Telegram</div>
              <div className="text-[11.5px]">
                <StatusLine
                  configured
                  identity={tgIdentity}
                  subtitle={tgMeta.bot_name ? `Connected · ${tgMeta.bot_name}` : "Telegram Connected"}
                  connectedLabel="Telegram Connected"
                />
              </div>
            </div>
            <Button size="sm" variant="outline" className="h-8 text-[12px]" disabled>
              Configured
            </Button>
          </div>
        ) : (
          <>
            <TelegramConnectForm tg={tg} canEdit={canEdit} testIdPrefix="onboarding-telegram" />
            <div className="rounded-md bg-indigo-50 ring-1 ring-indigo-200 px-3 py-2 text-[11px] text-indigo-800 inline-flex items-start gap-1.5">
              <ShieldCheck size={11} className="mt-0.5 shrink-0" />
              <span>Tokens are encrypted in your workspace. OpenClaw routes Telegram traffic to your agent inbox.</span>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <OnboardingStepBadge stepId={5} />
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">Connect Telegram</div>
      <p className="text-[13px] text-slate-600 mt-1">
        Connect your Telegram bot to allow your agent to receive and respond to Telegram messages.
      </p>
      {tgContent}
      <div className="mt-3 text-center">
        <span className="text-[11px] text-slate-400">
          Skip — you can connect Telegram anytime from Settings → Connections.
        </span>
      </div>
    </div>
  );
}

export function StepLinkedIn() {
  return (
    <div>
      <OnboardingStepBadge stepId={7} />
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">LinkedIn</div>
      <p className="text-[13px] text-slate-600 mt-1">
        LinkedIn profile connection is not part of this release. Skip this step and continue with Gmail, WhatsApp, and Telegram.
      </p>
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/80 p-5 flex items-start gap-3" data-testid="onboarding-linkedin-deferred">
        <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-sky-100 text-sky-700 shrink-0">
          <Linkedin size={20} />
        </div>
        <div>
          <div className="text-[14px] font-semibold text-slate-900">Coming in a later release</div>
          <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">
            No setup is required here. Click <span className="font-medium">Continue</span> below to move on.
          </p>
        </div>
      </div>
    </div>
  );
}

export function StepGmailCalendar() {
  const maya = useAssistantName();
  const {
    getRow, getStatus, isConfigured, loading, error, refresh, canEdit, ensureConnection,
  } = useBackendConnections();
  const gmailRow = getRow("gmail");
  const gmailConfigured = isConfigured("gmail");
  const calendarConfigured = isConfigured("calendar");
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [oauthStatusLoading, setOauthStatusLoading] = useState(false);

  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    setOauthStatusLoading(true);
    oauthApi.status()
      .then((s) => { if (!cancelled) setOauthStatus(s); })
      .catch((e) => {
        if (!cancelled) {
          setOauthStatus(null);
          // eslint-disable-next-line no-console
          console.warn("Google OAuth status check failed", e?.message);
        }
      })
      .finally(() => { if (!cancelled) setOauthStatusLoading(false); });
    return () => { cancelled = true; };
  }, [canEdit]);

  const oauthBlocked = oauthStatus != null && !oauthStatus.ready;

  let disabledReason;
  if (!canEdit) {
    disabledReason = "Only workspace admins can connect Gmail and Google Calendar";
  } else if (oauthStatusLoading) {
    disabledReason = "Checking Google OAuth configuration…";
  } else {
    disabledReason = null;
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const oauthError = params.get("error");
    if (!connected && !oauthError) return;

    if (connected === "gmail") {
      refresh().then(() => {
        const params2 = new URLSearchParams(window.location.search);
        if (params2.get("mock") === "1") {
          toast.message("Dev mock OAuth completed — add real Google credentials for production Gmail.");
        } else {
          toast.success("Gmail and Google Calendar connected");
        }
      });
    } else if (oauthError) {
      const messages = {
        oauth_not_configured: "Google OAuth is not configured on this server",
        token_exchange_failed: "Google sign-in failed — try again",
        missing_params: "Google sign-in was interrupted",
        unknown_state: "Sign-in session expired — try again",
        access_denied: "Google blocked sign-in — add your email as a test user in Google Cloud Console, or complete app verification for kadellabs.com",
        org_internal: "This Google account is not allowed — use a test user on the OAuth app or complete Google verification",
      };
      toast.error(messages[oauthError] || `Google sign-in failed (${oauthError})`);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [refresh]);

  const connectGmail = async () => {
    if (!canEdit) {
      toast.error("Only workspace admins can connect Gmail");
      return;
    }
    setOauthBusy(true);
    try {
      if (!gmailRow?.id) {
        await ensureConnection("gmail");
      }
      const r = await oauthApi.startGoogle("/onboarding/6");
      if (r?.auth_url) {
        window.location.href = r.auth_url;
        return;
      }
      toast.error("Google OAuth is not configured on this server");
    } catch (e) {
      const msg = e?.message || "Couldn't start Google sign-in";
      toast.error(msg);
      if (e?.code === "oauth_not_configured" || e?.status === 412 || e?.status === 503) {
        // eslint-disable-next-line no-console
        console.warn("Google OAuth not configured — set GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET in backend/.env, then restart the backend");
      }
    } finally {
      setOauthBusy(false);
    }
  };

  const rows = [
    {
      id: "gmail",
      label: "Gmail",
      Icon: Mail,
      accent: "bg-rose-100 text-rose-700",
      configured: gmailConfigured,
      status: getStatus("gmail"),
      identity: gmailDisplayIdentity(gmailRow),
      subtitle: gmailVerifiedSubtitle(gmailRow),
      blurb: "Read, draft, and send as you — connect via Google OAuth.",
      onConnect: connectGmail,
    },
    {
      id: "calendar",
      label: "Google Calendar",
      Icon: Calendar,
      accent: "bg-indigo-100 text-indigo-700",
      configured: calendarConfigured,
      status: getStatus("calendar"),
      identity: gmailConfigured ? gmailDisplayIdentity(gmailRow) : null,
      subtitle: gmailConfigured ? gmailVerifiedSubtitle(gmailRow) : null,
      blurb: `Included with Gmail — ${maya} books demos in your calendar.`,
      onConnect: connectGmail,
    },
  ];

  return (
    <div>
      <OnboardingStepBadge stepId={6} />
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">Your inbox and meetings</div>
      <p className="text-[13px] text-slate-600 mt-1">
        {maya} reads replies, drafts threads, and books demos. OAuth — scope-controlled by you.
      </p>
      {oauthBlocked && !gmailConfigured && (
        <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-md px-3 py-2 space-y-1">
          <div className="font-semibold">Google OAuth is not available yet</div>
          <div>{oauthStatus.message}</div>
          {oauthStatus.redirect_uri && (
            <div className="text-amber-700">Redirect URI: <code className="text-[10px]">{oauthStatus.redirect_uri}</code></div>
          )}
          <div className="text-amber-700 pt-0.5">
            After updating <code className="text-[10px]">backend/.env</code>, restart the backend — hot reload does not pick up env changes.
          </div>
          {Array.isArray(oauthStatus.setup_instructions) && oauthStatus.setup_instructions.length > 0 && (
            <ol className="list-decimal list-inside text-amber-700 space-y-0.5 pt-1">
              {oauthStatus.setup_instructions.slice(0, 4).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
          )}
        </div>
      )}
      {oauthStatus?.mock_mode && !gmailConfigured && (
        <div className="mt-3 text-[11px] text-indigo-800 bg-indigo-50 ring-1 ring-indigo-200 rounded-md px-3 py-2">
          Development mock OAuth is enabled — Connect Gmail simulates sign-in locally and will{" "}
          <span className="font-semibold">not</span> count as connected until real Google credentials are configured.
        </div>
      )}
      {oauthStatus?.mock_mode && gmailRow?.oauth_dev_mock && !gmailConfigured && (
        <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-md px-3 py-2">
          A dev mock Gmail session exists but is not a real Google connection. Click Connect Gmail
          after adding Google OAuth credentials, or ignore this placeholder in local dev.
        </div>
      )}
      {(() => {
        if (loading) return <LoadingRow />;
        if (error) return <ErrorRow message={error} onRetry={refresh} />;
        return (
          <div className="mt-5 space-y-2.5">
            {rows.map((r) => {
              const statusLabel = oauthStatusLoading ? "Checking…" : "Connecting…";
              const connectLabel = r.id === "calendar" ? "Connect via Gmail" : "Connect Gmail";
              return (
                <div
                  key={r.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3"
                  data-testid={`onboarding-row-${r.id}`}
                >
                  <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", r.accent)}>
                    <r.Icon size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-slate-900">{r.label}</div>
                    <div className="text-[11.5px]">
                      <StatusLine
                        configured={r.configured}
                        identity={r.identity}
                        subtitle={r.subtitle}
                        pendingLabel="Not Connected"
                        connectedLabel="Connected"
                      />
                    </div>
                  </div>
                  {r.configured ? (
                    <Button size="sm" variant="outline" className="h-8 text-[11.5px]" disabled>
                      Connected
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="h-8 text-[11.5px]"
                      disabled={!canEdit || oauthBusy || oauthStatusLoading}
                      onClick={r.onConnect}
                      data-testid={`onboarding-pair-${r.id}`}
                    >
                      {oauthBusy || oauthStatusLoading ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Loader2 size={12} className="animate-spin" />
                          {statusLabel}
                        </span>
                      ) : connectLabel}
                    </Button>
                  )}
                </div>
              );
            })}
            {disabledReason && !gmailConfigured && !calendarConfigured && (
              <p className="text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2.5 py-1.5">
                {disabledReason}
              </p>
            )}
          </div>
        );
      })()}
      <div className="mt-3 text-center">
        <span className="text-[11px] text-slate-400">
          After Google sign-in you&apos;ll return here. Revisit anytime under Settings → Connections.
        </span>
      </div>
    </div>
  );
}

function redditDisplayIdentity(row) {
  if (!row?.configured) return null;
  const r = row.channel_config?.reddit_oauth || {};
  const name = row.reddit_username || r.reddit_username || row.account_identifier;
  if (!name || r.dev_mock) return null;
  return name.startsWith("u/") ? name : `u/${name}`;
}

export function StepReddit() {
  const maya = useAssistantName();
  const { getRow, isConfigured, loading, error, refresh, canEdit, ensureConnection } = useBackendConnections();
  const row = getRow("reddit");
  const configured = isConfigured("reddit");
  const [oauthBusy, setOauthBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const oauthError = params.get("error");
    if (!connected && !oauthError) return;
    if (connected === "reddit") {
      refresh().then(() => toast.success("Reddit connected"));
    } else if (oauthError) {
      toast.error(`Reddit sign-in failed (${oauthError})`);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [refresh]);

  const connectReddit = async () => {
    if (!canEdit) {
      toast.error("Only workspace admins can connect Reddit");
      return;
    }
    setOauthBusy(true);
    try {
      if (!row?.id) await ensureConnection("reddit");
      const r = await oauthApi.startReddit("/onboarding/8");
      if (r?.auth_url) {
        window.location.href = r.auth_url;
        return;
      }
      toast.error("Reddit OAuth is not configured on this server");
    } catch (e) {
      toast.error(e?.message || "Couldn't start Reddit sign-in");
    } finally {
      setOauthBusy(false);
    }
  };

  return (
    <div>
      <OnboardingStepBadge stepId={8} />
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">Your Reddit presence</div>
      <p className="text-[13px] text-slate-600 mt-1">
        Connect Reddit so {maya} can monitor subreddits, draft replies, and sync DMs when OpenClaw is enabled.
      </p>
      {(() => {
        if (loading) return <LoadingRow />;
        if (error) return <ErrorRow message={error} onRetry={refresh} />;
        return (
          <div className="mt-5 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-orange-50/30 p-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-orange-100 text-orange-700">
                <MessageCircle size={20} />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-slate-900">Reddit</div>
                <div className="text-[11.5px]">
                  <StatusLine
                    configured={configured}
                    identity={redditDisplayIdentity(row)}
                    pendingLabel="Not connected — authorize via Reddit OAuth"
                  />
                </div>
              </div>
              {!configured ? (
                <Button
                  size="sm"
                  className="h-8 text-[12px]"
                  disabled={!canEdit || oauthBusy || !row?.id}
                  onClick={connectReddit}
                  data-testid="onboarding-connect-reddit"
                >
                  {oauthBusy ? <Loader2 size={12} className="animate-spin" /> : "Connect Reddit"}
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="h-8 text-[12px]" disabled>
                  Connected
                </Button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export function StepQuora() {
  const maya = useAssistantName();
  const { getRow, isConfigured, loading, error, refresh, canEdit, ensureConnection } = useBackendConnections();
  const row = getRow("quora");
  const configured = isConfigured("quora");
  const quora = useQuoraConfigure({
    connectionId: row?.id,
    ensureConnection,
    canEdit,
    onConfigured: async () => { await refresh(); },
  });

  const qIdentity = configured
    ? (row?.channel_config?.quora?.display_name || row?.account_identifier)
    : null;

  return (
    <div>
      <OnboardingStepBadge stepId={9} />
      <div className="mt-1 text-[22px] font-semibold tracking-tight text-slate-900">Your Quora profile</div>
      <p className="text-[13px] text-slate-600 mt-1">
        Link your Quora profile so {maya} can surface questions worth answering in your voice.
      </p>
      {(() => {
        if (loading) return <LoadingRow />;
        if (error) return <ErrorRow message={error} onRetry={refresh} />;
        if (configured) return (
          <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/40 p-5">
            <StatusLine configured identity={qIdentity} connectedLabel="Quora connected" />
          </div>
        );
        return (
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
            <QuoraConnectForm quora={quora} canEdit={canEdit} testIdPrefix="onboarding-quora" />
          </div>
        );
      })()}
    </div>
  );
}
